/**
 * Endereço de entrega do pedido de venda — mesma regra do backend
 * (domain.MissingShippingAddressFields), espelhada aqui para a tela avisar ANTES de enviar,
 * no mesmo espírito de `missingInvoiceFields` em pages/events/invitationUtils.ts.
 *
 * O backend continua sendo a autoridade: esta cópia existe para dar uma mensagem imediata,
 * não para substituir a validação. Foi justamente por a validação viver só no front que o
 * pedido 29734 nasceu sem CEP.
 */

export interface ShippingAddressFields {
  ship_addr_street?: string | null;
  ship_addr_num?: string | null;
  ship_addr_district?: string | null;
  ship_addr_city?: string | null;
  ship_addr_state?: string | null;
  ship_addr_zip?: string | null;
}

/**
 * Normaliza o CEP para os 8 dígitos que a coluna nvarchar(8) aceita.
 *
 * Sem isto, o campo com `maxLength={8}` corta "04037-002" em "04037-00": um CEP de 7 dígitos
 * que passa por preenchido, atravessa toda a validação e só falha na transportadora, que
 * devolve zero cotações sem erro nenhum.
 */
export const normalizeCep = (value: string): string => value.replace(/\D/g, '').slice(0, 8);

/** Um CEP só serve para cotar frete e emitir NF-e se tiver os 8 dígitos. */
export const isValidCep = (value?: string | null): boolean => normalizeCep(value ?? '').length === 8;

/**
 * Rótulos dos campos de entrega que faltam, na mesma ordem do backend — a lista vira texto na
 * tela ("Endereço de entrega incompleto — falta: número, CEP").
 */
export const missingShippingAddressFields = (a: ShippingAddressFields): string[] => {
  const missing: string[] = [];
  const check = (v: string | null | undefined, label: string) => {
    if (!v || !v.trim()) missing.push(label);
  };
  check(a.ship_addr_street, 'logradouro');
  check(a.ship_addr_num, 'número');
  check(a.ship_addr_district, 'bairro');
  check(a.ship_addr_city, 'cidade');
  check(a.ship_addr_state, 'UF');
  if (!isValidCep(a.ship_addr_zip)) missing.push('CEP');
  return missing;
};

/** Mensagem pronta para a tela, ou null quando o endereço está completo. */
export const shippingAddressError = (a: ShippingAddressFields): string | null => {
  const missing = missingShippingAddressFields(a);
  return missing.length === 0 ? null : `Endereço de entrega incompleto — falta: ${missing.join(', ')}.`;
};
